import { API_BASE } from '../api';

function esc(s: string): string {
	const d = document.createElement('div');
	d.textContent = s ?? '';
	return d.innerHTML;
}

function injectStyles(): void {
	if (document.getElementById('sp-draft-styles')) return;
	const style = document.createElement('style');
	style.id = 'sp-draft-styles';
	style.textContent = supportProfileDraftStyles();
	document.head.appendChild(style);
}

function shell(title: string): HTMLElement {
	document.title = title;
	injectStyles();

	// Override the dashboard layout so the public-link page fills the viewport.
	// #app is normally display:flex (Discord row layout) with overflow:hidden.
	// body has a dark background and fixed height. We need to undo all of that.
	document.body.classList.add('sp-public-body');
	const root = (document.getElementById('app') ?? document.body) as HTMLElement;
	root.classList.add('sp-public-root');
	root.innerHTML = '';

	// Hide any sidebar-scrim, modal-overlay, toast left from the dashboard shell
	const hide = (id: string): void => {
		const el = document.getElementById(id);
		if (el) el.style.display = 'none';
	};
	hide('sidebar-scrim');
	hide('modal-overlay');
	hide('toast');

	const page = document.createElement('div');
	page.className = 'sp-page';
	root.appendChild(page);
	return page;
}

export function supportProfileDraftStyles(): string {
	return `
    /* ── Body/root overrides ── reset dashboard layout for public pages */
    body.sp-public-body {
      display: block !important;
      height: auto !important;
      min-height: 100dvh;
      background: #fffaf0 !important;
      overflow-y: auto !important;
    }
    .sp-public-root {
      display: block !important;
      width: 100% !important;
      height: auto !important;
      min-height: 0 !important;
      overflow: visible !important;
      flex: none !important;
    }

    /* ── Page shell ── */
    .sp-page {
      min-height: 100dvh;
      width: 100%;
      background: #fffaf0;
      color: #18352a;
      font-family: system-ui, -apple-system, 'Segoe UI', sans-serif;
      padding: 24px 16px 48px;
      box-sizing: border-box;
    }
    .sp-shell { width: 100%; max-width: 640px; margin: 0 auto; }

    /* ── Brand ── */
    .sp-brand {
      color: #15803d;
      font-size: 15px;
      font-weight: 700;
      letter-spacing: 0.01em;
      margin-bottom: 20px;
    }

    /* ── Hero ── */
    .sp-hero { margin-bottom: 24px; }
    .sp-hero h1 {
      font-size: 28px;
      line-height: 1.2;
      margin: 0 0 10px;
      color: #18352a;
      font-weight: 800;
    }
    .sp-hero p {
      font-size: 16px;
      line-height: 1.55;
      margin: 0;
      color: #52665d;
    }

    /* ── Dev notice ── */
    .sp-notice {
      background: #f1f8ee;
      border: 1px solid #c8dfc1;
      border-radius: 12px;
      padding: 16px;
      color: #2d4a3a;
      font-size: 14px;
      line-height: 1.55;
      margin-bottom: 24px;
    }

    /* ── Progress ── */
    .sp-progress { display: flex; gap: 8px; margin: 0 0 24px; }
    .sp-progress span {
      height: 6px;
      flex: 1;
      border-radius: 999px;
      background: #d6e8d1;
    }
    .sp-progress span.sp-active { background: #16a34a; }

    /* ── Card sections ── */
    .sp-section {
      background: #fff;
      border: 1px solid #d8e7d0;
      border-radius: 16px;
      padding: 24px 20px;
      box-shadow: 0 2px 12px rgba(29, 79, 50, 0.06);
      margin-bottom: 24px;
    }
    .sp-section-kicker {
      color: #15803d;
      font-size: 13px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      margin-bottom: 6px;
    }
    .sp-section h2 {
      font-size: 22px;
      font-weight: 700;
      margin: 0 0 8px;
      color: #18352a;
    }
    .sp-section-copy {
      margin: 0 0 20px;
      color: #52665d;
      font-size: 15px;
      line-height: 1.55;
    }

    /* ── Form grid ── */
    .sp-form-grid { display: grid; grid-template-columns: 1fr; gap: 16px; }
    .sp-field {
      display: flex;
      flex-direction: column;
      gap: 6px;
      font-size: 14px;
      color: #3d5a4c;
      font-weight: 600;
    }
    .sp-field input, .sp-field textarea {
      width: 100%;
      min-height: 48px;
      border: 1.5px solid #c9dcc1;
      border-radius: 10px;
      background: #fafdf8;
      color: #18352a;
      font: inherit;
      font-weight: 500;
      font-size: 16px;
      padding: 12px 14px;
      box-sizing: border-box;
      outline: none;
      transition: border-color 0.15s, box-shadow 0.15s;
      -webkit-appearance: none;
    }
    .sp-field textarea { resize: vertical; min-height: 100px; }
    .sp-field input:focus, .sp-field textarea:focus {
      border-color: #16a34a;
      box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.12);
    }

    /* ── Actions ── */
    .sp-actions { display: flex; flex-direction: column; gap: 10px; margin-top: 24px; }
    .sp-button {
      min-height: 52px;
      border: none;
      border-radius: 12px;
      background: #16a34a;
      color: #fff;
      font-size: 16px;
      font-weight: 700;
      cursor: pointer;
      padding: 14px 20px;
      transition: background 0.15s;
      -webkit-appearance: none;
      text-align: center;
    }
    .sp-button:active { background: #128a3e; }
    .sp-button-secondary {
      background: transparent;
      color: #15803d;
      border: 1.5px solid #c9dcc1;
    }
    .sp-button-secondary:active { background: #f1f8ee; }

    /* ── Display grid (read-only fields) ── */
    .sp-display-grid { display: grid; grid-template-columns: 1fr; gap: 12px; margin-bottom: 20px; }
    .sp-display-item {
      background: #f4faef;
      border: 1px solid #d8e7d0;
      border-radius: 10px;
      padding: 14px;
    }
    .sp-display-item span {
      display: block;
      color: #52665d;
      font-size: 13px;
      font-weight: 600;
      margin-bottom: 4px;
    }
    .sp-display-item strong { color: #18352a; font-size: 16px; }

    /* ── Radio/checkbox groups ── */
    .sp-choice {
      border: 1.5px solid #d8e7d0;
      border-radius: 12px;
      padding: 16px;
      margin: 20px 0;
    }
    .sp-choice legend {
      color: #3d5a4c;
      font-size: 14px;
      font-weight: 700;
      padding: 0 6px;
    }
    .sp-choice label, .sp-checks label {
      min-height: 48px;
      display: flex;
      align-items: flex-start;
      gap: 12px;
      line-height: 1.5;
      padding: 4px 0;
      font-size: 15px;
      color: #18352a;
    }
    .sp-choice input, .sp-checks input {
      width: 22px;
      height: 22px;
      accent-color: #16a34a;
      flex: 0 0 auto;
      margin-top: 2px;
    }

    /* ── Agreement terms ── */
    .sp-terms { display: grid; gap: 0; margin-top: 20px; }
    .sp-terms section {
      border-top: 1px solid #e4eee0;
      padding: 16px 0;
    }
    .sp-terms section:last-child { padding-bottom: 0; }
    .sp-terms h3 {
      font-size: 15px;
      font-weight: 700;
      margin: 0 0 6px;
      color: #18352a;
    }
    .sp-terms p {
      margin: 0;
      color: #52665d;
      font-size: 14px;
      line-height: 1.6;
    }

    /* ── Deletion note ── */
    .sp-delete-note {
      background: #f4faef;
      border-radius: 10px;
      padding: 16px;
      margin-top: 20px;
      line-height: 1.55;
      font-size: 14px;
      color: #52665d;
    }
    .sp-delete-note a { color: #15803d; font-weight: 700; text-decoration: underline; }

    /* ── Acknowledgement checkboxes ── */
    .sp-checks { display: grid; gap: 12px; margin-top: 20px; }

    /* ── Draft note ── */
    .sp-draft-note {
      color: #7a9185;
      font-size: 13px;
      line-height: 1.55;
      margin-top: 16px;
      text-align: center;
    }

    /* ── Desktop enhancement ── */
    @media (min-width: 680px) {
      .sp-page { padding: 48px 24px 64px; }
      .sp-shell { max-width: 720px; }
      .sp-hero h1 { font-size: 36px; }
      .sp-form-grid, .sp-display-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
      .sp-field-full { grid-column: 1 / -1; }
      .sp-actions { flex-direction: row; }
      .sp-button { width: auto; min-width: 180px; }
    }
  `;
}

export function supportProfileFormHtml(): string {
	return `
    <section class="sp-section" aria-labelledby="sp-profile-title">
      <div class="sp-section-kicker">Step 1 of 2</div>
      <h2 id="sp-profile-title">Support person profile</h2>
      <p class="sp-section-copy">Share the details we need for support coordination, records, and invoicing.</p>
      <div class="sp-form-grid">
        <label class="sp-field">
          <span>Full legal name</span>
          <input name="full_legal_name" type="text" autocomplete="name" />
        </label>
        <label class="sp-field">
          <span>Preferred name</span>
          <input name="preferred_name" type="text" autocomplete="nickname" />
        </label>
        <label class="sp-field">
          <span>Phone</span>
          <input name="phone" type="tel" autocomplete="tel" />
        </label>
        <label class="sp-field">
          <span>Email</span>
          <input name="email" type="email" autocomplete="email" />
        </label>
        <label class="sp-field sp-field-full">
          <span>Address</span>
          <input name="address" type="text" autocomplete="street-address" />
        </label>
        <label class="sp-field">
          <span>ABN</span>
          <input name="abn" type="text" inputmode="numeric" />
        </label>
        <label class="sp-field">
          <span>Trading name (optional)</span>
          <input name="business_provider_name" type="text" />
        </label>
        <label class="sp-field">
          <span>Bank account name</span>
          <input name="bank_account_name" type="text" autocomplete="off" />
        </label>
        <label class="sp-field">
          <span>BSB</span>
          <input name="bsb" type="text" inputmode="numeric" autocomplete="off" />
        </label>
        <label class="sp-field">
          <span>Account number</span>
          <input name="account_number" type="text" inputmode="numeric" autocomplete="off" />
        </label>
        <label class="sp-field">
          <span>Invoice email if different</span>
          <input name="invoice_email" type="email" autocomplete="email" />
        </label>
        <label class="sp-field sp-field-full">
          <span>Notes</span>
          <textarea name="notes" rows="4"></textarea>
        </label>
      </div>
      <div class="sp-actions">
        <button class="sp-button" type="button">Continue</button>
        <button class="sp-button sp-button-secondary" type="button">Save draft preview</button>
      </div>
      <p class="sp-draft-note">Your details are stored for invoicing only.</p>
    </section>
  `;
}

// DOM name === field key, except trading_name lives on input "business_provider_name".
const SP_FIELDS: Array<[string, string]> = [
	['full_legal_name', 'Full legal name'],
	['preferred_name', 'Preferred name'],
	['trading_name', 'Trading name'],
	['phone', 'Phone'],
	['email', 'Email'],
	['address', 'Address'],
	['abn', 'ABN'],
	['bank_account_name', 'Bank account name'],
	['bsb', 'BSB'],
	['account_number', 'Account number'],
	['invoice_email', 'Invoice email'],
	['notes', 'Notes'],
];
// Fields that block submit — shown with a * in the form and validated on Continue.
const SP_REQUIRED: Array<[string, string]> = [
	['full_legal_name', 'Full legal name'],
	['abn', 'ABN'],
	['email', 'Email'],
	['bank_account_name', 'Bank account name'],
	['bsb', 'BSB'],
	['account_number', 'Account number'],
];

function spDomName(field: string): string {
	return field === 'trading_name' ? 'business_provider_name' : field;
}

export function renderSupportProfileSetup(): void {
	const page = shell('Support profile setup - the hub ecosystems');
	renderSetupForm(page, {});
}

function renderSetupForm(page: HTMLElement, values: Record<string, string>): void {
	page.innerHTML = `
    <main class="sp-shell">
      <div class="sp-brand">the hub ecosystems</div>
      <header class="sp-hero">
        <h1>Support profile setup</h1>
        <p>Enter your details, review, then submit. One-time setup.</p>
      </header>
      <div class="sp-notice">Your information is used only for support coordination, records, and invoicing. <strong>*</strong> marks a required field.</div>
      <div class="sp-progress" aria-label="Progress"><span class="sp-active"></span><span></span></div>
      ${supportProfileFormHtml()}
      <div id="sp-result"></div>
    </main>
  `;
	for (const [field] of SP_FIELDS) {
		const el = page.querySelector(`[name="${spDomName(field)}"]`) as HTMLInputElement | HTMLTextAreaElement | null;
		if (el && values[field] != null) el.value = values[field];
	}
	// Mark required fields with a * (scoped to this page, not the shared form).
	for (const [field] of SP_REQUIRED) {
		const span = page.querySelector(`[name="${spDomName(field)}"]`)?.closest('.sp-field')?.querySelector('span');
		if (span && !(span.textContent || '').includes('*')) span.textContent = `${span.textContent} *`;
	}
	const btns = page.querySelectorAll('.sp-button');
	const cont = btns[0] as HTMLButtonElement | undefined;
	const sec = btns[1] as HTMLElement | undefined;
	if (sec) sec.style.display = 'none';
	if (!cont) return;
	cont.textContent = 'Continue';
	cont.addEventListener('click', () => {
		const v = collectSetupValues(page);
		const missing = SP_REQUIRED.filter(([n]) => !v[n]);
		const r = page.querySelector('#sp-result') as HTMLElement;
		if (missing.length) {
			r.innerHTML = `<div class="sp-notice" style="border-color:#f1a9a0;background:#fdecea;color:#8a2c1e">Please complete the required field(s): ${missing.map(([, l]) => esc(l)).join(', ')}.</div>`;
			return;
		}
		renderSetupReview(page, v);
	});
}

function collectSetupValues(page: HTMLElement): Record<string, string> {
	const out: Record<string, string> = {};
	for (const [field] of SP_FIELDS) {
		const el = page.querySelector(`[name="${spDomName(field)}"]`) as HTMLInputElement | HTMLTextAreaElement | null;
		out[field] = el?.value?.trim() || '';
	}
	return out;
}

function reviewGroup(title: string, fields: Array<[string, string]>, v: Record<string, string>): string {
	const rows = fields.filter(([n]) => v[n]).map(([n, label]) =>
		`<div class="sp-display-item"><span>${esc(label)}</span><strong>${esc(v[n])}</strong></div>`).join('');
	return rows ? `<div class="sp-section-kicker" style="margin-top:18px">${esc(title)}</div><div class="sp-display-grid">${rows}</div>` : '';
}

function renderSetupReview(page: HTMLElement, v: Record<string, string>): void {
	const fromName = v.trading_name ? `${v.full_legal_name} (trading as ${v.trading_name})` : v.full_legal_name;
	const contact = [v.phone, v.email].filter(Boolean).map(esc).join(' · ');
	page.innerHTML = `
    <main class="sp-shell">
      <div class="sp-brand">the hub ecosystems</div>
      <header class="sp-hero">
        <h1>Review your details</h1>
        <p>This is how you'll appear as the provider — the "From" on every invoice. Check it's correct, then submit.</p>
      </header>
      <div class="sp-progress" aria-label="Progress"><span class="sp-active"></span><span class="sp-active"></span></div>
      <section class="sp-section">
        <div class="sp-section-kicker">Step 2 of 2 · Review</div>
        <h2>Invoice "From" — your details</h2>
        <div style="border:1.5px solid #c9dcc1;border-radius:12px;padding:16px;background:#fafdf8">
          <div style="font-weight:800;font-size:17px;color:#18352a">${esc(fromName)}</div>
          ${v.abn ? `<div style="color:#52665d;font-size:14px;margin-top:3px">ABN ${esc(v.abn)}</div>` : ''}
          ${v.address ? `<div style="color:#52665d;font-size:14px;margin-top:3px">${esc(v.address)}</div>` : ''}
          ${contact ? `<div style="color:#52665d;font-size:14px;margin-top:3px">${contact}</div>` : ''}
        </div>
        ${reviewGroup('Payment details', [['bank_account_name', 'Account name'], ['bsb', 'BSB'], ['account_number', 'Account number']], v)}
        ${reviewGroup('Other', [['preferred_name', 'Preferred name'], ['invoice_email', 'Invoice email'], ['notes', 'Notes']], v)}
      </section>
      <div id="sp-result"></div>
      <div class="sp-actions">
        <button class="sp-button sp-button-secondary" id="sp-back" type="button">Back to edit</button>
        <button class="sp-button" id="sp-submit" type="button">Submit profile</button>
      </div>
    </main>
  `;
	(page.querySelector('#sp-back') as HTMLButtonElement).addEventListener('click', () => renderSetupForm(page, v));
	(page.querySelector('#sp-submit') as HTMLButtonElement).addEventListener('click', (e) => {
		void doSetupSubmit(page, e.currentTarget as HTMLButtonElement, v);
	});
}

async function doSetupSubmit(page: HTMLElement, btn: HTMLButtonElement, v: Record<string, string>): Promise<void> {
	btn.disabled = true;
	btn.textContent = 'Submitting…';
	try {
		const res = await fetch(`${API_BASE}/api/ndis/public/provider-setup`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(v),
		});
		const json = await res.json();
		if (json.status !== 'ok') throw new Error(json.error || 'Submit failed');
		(page.querySelector('.sp-shell') as HTMLElement).innerHTML = `<div class="sp-brand">the hub ecosystems</div>
      <section class="sp-section"><h2>Thanks, ${esc(v.preferred_name || v.full_legal_name)}</h2>
      <p class="sp-section-copy">Your support profile has been submitted and can now be invoiced against. You can close this page.</p></section>`;
	} catch (e) {
		btn.disabled = false;
		btn.textContent = 'Submit profile';
		const r = page.querySelector('#sp-result') as HTMLElement | null;
		if (r) r.innerHTML = `<div class="sp-notice" style="border-color:#f1a9a0;background:#fdecea;color:#8a2c1e">Could not submit: ${esc((e as Error).message)}</div>`;
	}
}

export function isSupportProfileSetupPath(): boolean {
	return /\/support-profile\/setup\/?/.test(window.location.pathname);
}
